"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ExportManager = void 0;
const sdk_geometry_api_sdk_v2_1 = require("@shapediver/sdk.geometry-api-sdk-v2");
const viewer_shared_services_1 = require("@shapediver/viewer.shared.services");
const viewer_shared_types_1 = require("@shapediver/viewer.shared.types");
const Export_1 = require("../dto/Export");
/**
 * Manager for exports and export related operations.
 *
 * The manager is created by the SessionEngineCore and can be accessed
 * via the `exportManager` property.
 */
class ExportManager {
    constructor(sessionEngineCore) {
        this._eventEngine = viewer_shared_services_1.EventEngine.instance;
        this._exports = {};
        this._uuidGenerator = viewer_shared_services_1.UuidGenerator.instance;
        this._sessionEngineCore = sessionEngineCore;
    }
    get exports() {
        return this._exports;
    }
    /**
     * Cleans the provided export parameters by converting names/displaynames to ids
     * and filling in missing parameters with current session values.
     *
     * @param parameters The provided export parameters
     * @returns The cleaned export parameters
     */
    cleanExportParameters(parameters) {
        const requestParameterSet = {};
        // first step, we convert all our names and displaynames to ids
        for (const parameterIdOrName in parameters) {
            // we prioritize id, then name and then displayname
            // if there are two parameters with the same name or displayname, we take the one that is found first (no way for us to evaluate which one the user meant)
            const parameterObject = Object.values(this._sessionEngineCore.parameterManager.parameters).find((p) => p.id === parameterIdOrName ||
                p.name === parameterIdOrName ||
                p.displayname === parameterIdOrName);
            // in case the key of the key value pair was neither the id, name or displayname, skip
            if (!parameterObject)
                continue;
            // copy into new dictionary
            requestParameterSet[parameterObject.id] = (" " + parameterObject.stringify(parameters[parameterIdOrName])).slice(1);
        }
        // seconds step, fill all other parameter values that are currently not set
        const currentParameters = this._sessionEngineCore.parameterManager.parameterValues;
        for (const parameterId in currentParameters) {
            // if already set by input values, skip
            if (requestParameterSet[parameterId] !== undefined)
                continue;
            // deep copy into new dictionary
            requestParameterSet[parameterId] = (" " + currentParameters[parameterId]).slice(1);
        }
        return requestParameterSet;
    }
    /**
     * Creates export objects from the current response DTO.
     *
     * Called internally after each customization or export request to update the exports.
     * Exports of type EMAIL and DOWNLOAD are created.
     *
     * Called by SessionEngineCore.updateResponseDto.
     */
    createExportsFromDto() {
        if (!this._sessionEngineCore.responseDto)
            return;
        for (const exportId in this._sessionEngineCore.responseDto.exports) {
            if (this._sessionEngineCore.responseDto.exports[exportId].type ===
                sdk_geometry_api_sdk_v2_1.ResExportDefinitionType.EMAIL ||
                this._sessionEngineCore.responseDto.exports[exportId].type ===
                    sdk_geometry_api_sdk_v2_1.ResExportDefinitionType.DOWNLOAD) {
                if (!this.exports[exportId]) {
                    this._sessionEngineCore.responseDto.exports[exportId].id =
                        exportId;
                    this.exports[exportId] = new Export_1.Export(this._sessionEngineCore.responseDto.exports[exportId], this._sessionEngineCore, this);
                }
                else {
                    this.exports[exportId].updateExport(this._sessionEngineCore.responseDto.exports[exportId]);
                }
            }
        }
    }
    /**
     * Requests an export.
     *
     * @param exportId The id of the export to request
     * @param parameters The parameters to use for the export
     * @param maxWaitTime The maximum time to wait for the export to complete
     * @param retry Whether to retry the request in case of failure
     * @returns Promise with the requested export
     */
    requestExport(exportId, parameters, maxWaitTime, retry = false) {
        return __awaiter(this, void 0, void 0, function* () {
            this._sessionEngineCore.utilsManager.checkAvailability("export");
            try {
                yield this._sessionEngineCore.fileUploadManager.uploadFileParameters(parameters);
                const requestParameterSet = this.cleanExportParameters(parameters);
                const responseDto = yield new sdk_geometry_api_sdk_v2_1.UtilsApi(this._sessionEngineCore.sdkConfig).submitAndWaitForExport(this._sessionEngineCore.sessionId, { exports: [exportId], parameters: requestParameterSet }, maxWaitTime, this._sessionEngineCore.parameterManager.ignoreUnknownParams);
                this._sessionEngineCore.updateResponseDto(responseDto);
                return this.exports[exportId];
            }
            catch (e) {
                yield this._sessionEngineCore.utilsManager.handleError(e, retry);
                return yield this.requestExport(exportId, parameters, maxWaitTime, true);
            }
        });
    }
    /**
     * Requests multiple exports.
     *
     * @param body The export request body
     * @param loadOutputs Whether to load outputs as part of the export request
     * @param maxWaitMsec The maximum time to wait for the export to complete
     * @param retry Whether to retry the request in case of failure
     * @returns Promise with the response DTO
     */
    requestExports(body, loadOutputs = false, maxWaitMsec, retry = false) {
        return __awaiter(this, void 0, void 0, function* () {
            let processId;
            const eventId = this._uuidGenerator.create();
            // if the outputs are loaded, we treat this as a customization by sending the same events
            const treatInternallyAsCustomization = loadOutputs === true &&
                this._sessionEngineCore.outputManager.allowOutputLoading === true;
            const eventInfo = {
                type: viewer_shared_types_1.TASK_TYPE.SESSION_CUSTOMIZATION,
                category: viewer_shared_types_1.TASK_CATEGORY.SESSION_CUSTOMIZATION.CUSTOMIZE_VIA_EXPORTS,
                id: eventId,
                data: { sessionId: this._sessionEngineCore.id },
            };
            this._sessionEngineCore.utilsManager.checkAvailability("export");
            try {
                if (treatInternallyAsCustomization) {
                    this._eventEngine.emitEvent(viewer_shared_services_1.EVENTTYPE.TASK.TASK_START, Object.assign(Object.assign({}, eventInfo), { progress: 0, status: "Customizing session" }));
                }
                // activate the busy mode if outputs are loaded
                if (loadOutputs === true &&
                    this._sessionEngineCore.outputManager.allowOutputLoading ===
                        true &&
                    body.outputs &&
                    Object.keys(body.outputs).length > 0) {
                    processId = this._uuidGenerator.create();
                    this._sessionEngineCore.utilsManager.addBusyMode(processId);
                }
                if (treatInternallyAsCustomization) {
                    this._eventEngine.emitEvent(viewer_shared_services_1.EVENTTYPE.TASK.TASK_PROCESS, Object.assign(Object.assign({}, eventInfo), { progress: 0.1, status: "Uploading file parameters" }));
                }
                yield this._sessionEngineCore.fileUploadManager.uploadFileParameters(body.parameters);
                const requestParameterSet = this.cleanExportParameters(body.parameters);
                if (treatInternallyAsCustomization) {
                    this._eventEngine.emitEvent(viewer_shared_services_1.EVENTTYPE.TASK.TASK_PROCESS, Object.assign(Object.assign({}, eventInfo), { progress: 0.1, status: "Sending customization request" }));
                }
                const responseDto = yield new sdk_geometry_api_sdk_v2_1.UtilsApi(this._sessionEngineCore.sdkConfig).submitAndWaitForExport(this._sessionEngineCore.sessionId, {
                    exports: body.exports,
                    parameters: requestParameterSet,
                    outputs: body.outputs,
                    max_wait_time: body.max_wait_time,
                }, maxWaitMsec, this._sessionEngineCore.parameterManager.ignoreUnknownParams);
                this._sessionEngineCore.updateResponseDto(responseDto);
                if (treatInternallyAsCustomization) {
                    yield this._sessionEngineCore.outputManager.updateOutputs(Object.assign(Object.assign({}, eventInfo), { progressRange: {
                            min: 0.1,
                            max: 0.9,
                        } }));
                    this._eventEngine.emitEvent(viewer_shared_services_1.EVENTTYPE.SESSION.SESSION_CUSTOMIZED, { sessionId: this._sessionEngineCore.id });
                    this._eventEngine.emitEvent(viewer_shared_services_1.EVENTTYPE.TASK.TASK_END, Object.assign(Object.assign({}, eventInfo), { progress: 1, status: "Session customized" }));
                }
                if (processId)
                    this._sessionEngineCore.utilsManager.removeBusyMode(processId);
                return responseDto;
            }
            catch (e) {
                if (treatInternallyAsCustomization) {
                    this._eventEngine.emitEvent(viewer_shared_services_1.EVENTTYPE.TASK.TASK_CANCEL, Object.assign(Object.assign({}, eventInfo), { progress: 1, status: "Session customization failed" }));
                }
                if (processId)
                    this._sessionEngineCore.utilsManager.removeBusyMode(processId);
                yield this._sessionEngineCore.utilsManager.handleError(e, retry);
                return yield this.requestExports(body, loadOutputs, maxWaitMsec, true);
            }
        });
    }
    /**
     * Save the export properties for displayname, order, tooltip and hidden
     *
     * @param exports
     * @returns
     */
    saveExportProperties(exports, retry = false) {
        return __awaiter(this, void 0, void 0, function* () {
            this._sessionEngineCore.utilsManager.checkAvailability("export-definition", true);
            try {
                yield new sdk_geometry_api_sdk_v2_1.ExportApi(this._sessionEngineCore.sdkConfig).updateExportDefinitions(this._sessionEngineCore.modelId, exports);
                return true;
            }
            catch (e) {
                yield this._sessionEngineCore.utilsManager.handleError(e, retry);
                return yield this.saveExportProperties(exports, true);
            }
        });
    }
}
exports.ExportManager = ExportManager;
//# sourceMappingURL=ExportManager.js.map